/**
 * BulkSetStatesModal — AUTOASSIGN-USABILITY-001 (Jul 2026)
 *
 * Admin / Team Leader action: apply the same Eligible States list to many
 * auto-assign processors at once. Includes a confirmation summary before
 * saving. The save is audited server-side (actor, before/after, timestamp).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, X, Check } from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const BulkSetStatesModal = ({ isOpen, onClose, selectedProcessors = [], onSaved }) => {
  const [states, setStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [stage, setStage] = useState('select'); // 'select' | 'confirm'
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedStates([]);
      setStage('select');
      setSaving(false);
      return;
    }
    axios
      .get(`${API}/api/auto-assignment/states`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then((r) => setStates(r.data?.states || []))
      .catch(() => setStates([]));
  }, [isOpen]);

  const toggleState = (code) =>
    setSelectedStates((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );

  const filteredStates = useMemo(() => states, [states]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/api/auto-assignment/users/bulk/eligible-states`,
        {
          user_ids: selectedProcessors.map((p) => p.id),
          eligible_states: selectedStates,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(
        `Eligible states updated for ${res.data.updated_count} processor(s)`,
        { description: `States: ${selectedStates.join(', ') || 'none'}` }
      );
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(
        `Failed to update: ${err?.response?.data?.detail || err.message}`
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="bulk-set-states-modal"
    >
      <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h2 className="text-base font-semibold text-gray-900">
            {stage === 'select' ? 'Set Eligible States' : 'Confirm Changes'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            data-testid="bulk-set-states-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {stage === 'select' && (
          <div className="px-5 py-4">
            <p className="mb-3 text-sm text-gray-600">
              Applying to{' '}
              <span className="font-semibold text-gray-900">
                {selectedProcessors.length} processor(s)
              </span>
              . Selecting no states will{' '}
              <span className="font-semibold text-red-600">disable auto-assignment</span>{' '}
              for them.
            </p>
            <div className="max-h-64 overflow-y-auto rounded-md border p-2">
              <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4">
                {filteredStates.map((s) => {
                  const on = selectedStates.includes(s.code);
                  return (
                    <button
                      key={s.code}
                      type="button"
                      onClick={() => toggleState(s.code)}
                      className={`rounded border px-2 py-1 text-xs font-medium transition-colors ${
                        on
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                      data-testid={`bulk-state-${s.code}`}
                    >
                      {s.code}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
              <span>{selectedStates.length} selected</span>
              <button
                type="button"
                className="underline hover:text-gray-700"
                onClick={() => setSelectedStates([])}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {stage === 'confirm' && (
          <div className="px-5 py-4">
            <div className="mb-3 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                This overwrites the eligible states for{' '}
                <b>{selectedProcessors.length}</b> processor(s) with{' '}
                {selectedStates.length ? (
                  <b>{selectedStates.join(', ')}</b>
                ) : (
                  <b className="text-red-600">no states (auto-assign disabled)</b>
                )}
                . This action is recorded in the audit log.
              </span>
            </div>
            <div className="max-h-40 overflow-y-auto rounded-md border">
              <ul className="divide-y text-sm">
                {selectedProcessors.map((p) => (
                  <li key={p.id} className="px-3 py-1.5 text-gray-700">
                    {p.name}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t px-5 py-3">
          {stage === 'select' ? (
            <>
              <Button variant="outline" onClick={onClose} data-testid="bulk-set-states-cancel">
                Cancel
              </Button>
              <Button
                onClick={() => setStage('confirm')}
                data-testid="bulk-set-states-next"
              >
                Review Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStage('select')}>
                Back
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="bulk-set-states-confirm"
              >
                <Check className="mr-1 h-4 w-4" />
                {saving ? 'Saving…' : 'Confirm & Save'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkSetStatesModal;
